import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { AppIcon } from '@backrs_ui/common-ui';

import { HorizontalMenuTabs } from '@backrs_ui/common-ui/src/atoms/HorizontalMenuTabs';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  ProfileFields,
  useFetchProfileValueOptionsQuery,
  UserProfileResponse,
  ProfileValueOption,
  StoryDocument,
  useFetchUserProfileInfoQuery,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Box, MenuItem, Select, SxProps, Theme, Typography, Button } from '@mui/material';

import { FIELD_CONFIGS } from './configs/field.config';
import { PAGE_CONFIGS, PageName } from './configs/section.config';
import { useGetSelectedOptions } from './hooks/useGetSelectedOptions';
import { useAgentContext } from './ProfilePage.agentContext';
import { ProfileTabSection } from './ProfileTabSection';
import {
  ProfileFieldConfig,
  ProfileFieldValue,
  ProfileValue,
  CreateFieldConfigProps,
} from './types/profile.types';
import { determineDefaultValue, handleFieldChange } from './utils/fields.helpers';
import { filterEditProfileProfileOptions } from './utils/options.helpers';

const STRINGS = appStrings.modules.profile.edit;

type ProfilePageProps = Readonly<{
  handleUpdatingAnswer: (
    key: string,
    value: ProfileValue[],
    displayProperty?: string,
    fieldConfig?: CreateFieldConfigProps,
  ) => Promise<void>;
  userProfile?: UserProfileResponse;
}>;

export const ProfilePage = ({ handleUpdatingAnswer, userProfile }: ProfilePageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { refetch: refetchUserProfile } = useFetchUserProfileInfoQuery([]);

  const { data: unfilteredProfileOptions } = useFetchProfileValueOptionsQuery(undefined);
  const profileOptions = filterEditProfileProfileOptions(unfilteredProfileOptions);

  const [activeTab, setActiveTab] = useState(0);
  const careerPaths = useAppSelector((state) => state.careerPath.careerPaths);
  const navigate = useNavigate();

  const selectedOptions = useGetSelectedOptions({
    userProfile,
    isEditView: true,
  });

  const tabs = Object.keys(PAGE_CONFIGS);
  const currentPage = tabs[activeTab] as PageName;
  const currentPageConfig = PAGE_CONFIGS[currentPage];

  useAgentContext(currentPage);

  const handleTabChange = (newTabIndex: number) => {
    setActiveTab(newTabIndex);
    const newTab = tabs[newTabIndex];

    setSearchParams({ tab: newTab });
  };

  useEffect(() => {
    const tabParam = searchParams.get('tab');

    if (tabParam) {
      const tabIndex = tabs.indexOf(tabParam as PageName);

      if (tabIndex !== -1) {
        setActiveTab(tabIndex);
      }
    }
  }, [searchParams, tabs]);

  const handleViewProfile = () => {
    navigate(`${APP_ROUTE.PROFILE}/${userProfile?.profile.publicProfile.id}`);
  };

  if (!profileOptions) return null;

  const PROFILE_OPTIONS = {
    ...profileOptions,
    careerPaths: careerPaths.map(({ id, name }) => ({ id, name })),
  };

  // Convert the field configs into complete ProfileFieldConfig array
  const fields: ProfileFieldConfig[] = Object.entries(FIELD_CONFIGS).map(([key, config]) => {
    const fieldKey = key as ProfileFields;
    const filteredOptions = selectedOptions(fieldKey).filter(
      (opt): opt is ProfileValueOption | string => opt !== undefined,
    );

    const defaultValue = determineDefaultValue({ config, filteredOptions });

    const fieldConfig: ProfileFieldConfig = {
      ...config,
      key: fieldKey,
      options: PROFILE_OPTIONS[fieldKey],
      defaultValue,
      onChange: (values: ProfileFieldValue[]) => {
        handleFieldChange({
          config,
          values,
          fieldKey,
          handleUpdatingAnswer,
          refetchUserProfile,
        });
      },
      ...(['DOCUMENT', 'STORY_VIDEO'].includes(config.type.variant)
        ? { storyDocuments: selectedOptions(fieldKey) as StoryDocument[] }
        : {}),
    };

    return fieldConfig;
  });

  return (
    <Box sx={pageContainerSx}>
      <Box sx={containerSx}>
        <Box sx={headerSx}>
          <Box sx={titleAndButtonSx}>
            <Typography variant="display-xs-semibold">{STRINGS.editProfile}</Typography>

            <Button
              id="profile-view-profile-button"
              sx={viewProfileButtonSx}
              hierarchy="Secondary gray"
              size="md"
              onClick={handleViewProfile}
            >
              {STRINGS.viewProfile}
            </Button>
          </Box>

          {/* HORIZONTAL MENU FOR LARGER SCREENS */}
          <Box sx={horizontalMenuSxLargerScreens}>
            <HorizontalMenuTabs
              id="profile-tabs"
              tabs={tabs}
              value={activeTab}
              setValue={handleTabChange}
              variant="box"
            />
          </Box>

          {/* SELECT FOR SMALL SCREENS */}
          <Box sx={phoneSizeScreenMenuDropdown}>
            <Select
              id="profile-tab-select"
              value={tabs[activeTab]}
              defaultValue={tabs[0]}
              sx={selectSx}
              IconComponent={({ className }) => {
                const isSelectOpen = className.includes('MuiSelect-iconOpen');

                return (
                  <Box sx={smallScreenHeaderSx(isSelectOpen)}>
                    <AppIcon iconName="ChevronDown" diameter="16px" color="Gray/600" />
                  </Box>
                );
              }}
            >
              {tabs.map((tab) => (
                <MenuItem key={tab} value={tab} onClick={() => handleTabChange(tabs.indexOf(tab))}>
                  <Typography variant="text-md-regular">{tab}</Typography>
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <ProfileTabSection
          pageConfig={currentPageConfig}
          fields={fields}
          profileOptions={PROFILE_OPTIONS}
        />
      </Box>
    </Box>
  );
};

const smallScreenHeaderSx =
  (isSelectOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    transition: 'all .3s ease-in-out',
    mb: isSelectOpen ? backrs.spacing['spacing-xs'] : `-${backrs.spacing['spacing-xs']}`,
    transform: isSelectOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  });

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: backrs.spacing['spacing-4xl'],
  gap: backrs.spacing['spacing-2xl'],
});

const pageContainerSx: SxProps<Theme> = {
  width: '100%',
  containerType: 'inline-size',
};

const containerSx: SxProps<Theme> = {
  overflowY: 'auto',
  width: '100%',
};

const horizontalMenuSxLargerScreens: SxProps<Theme> = {
  display: 'none',
  '@container (min-width: 900px)': {
    display: 'block',
  },
};

const phoneSizeScreenMenuDropdown: SxProps<Theme> = {
  display: 'block',
  '@container (min-width: 900px)': {
    display: 'none',
  },
};

const selectSx: SxProps<Theme> = ({ backrs }) => ({
  paddingX: backrs.spacing['spacing-md'],
});

const viewProfileButtonSx: SxProps<Theme> = {
  display: {
    xs: 'block',
    sm: 'none',
  },
};

const titleAndButtonSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
};
